/**
 * shareCard/storyLine — the one headline sentence on a share card.
 *
 * The seven-rule table from the night-wheel spec (2026-09-09, section 1a),
 * evaluated top to bottom; the FIRST rule that fires wins:
 *   1 new-set       "New set: first time you swam The Rockalita"
 *   2 fastest-set   "Fastest Rockalita yet: 4:31" / "beat 4:38 from Aug 2"
 *   3 new-sequence  "New sequence" / "Rockalita → Killer BS → IM"
 *   4 nth-time      "7th time swimming The Rockalita" / "best 4:31"
 *   5 pr-distance   "Farthest swim yet: 2,500 m" / "beat 2,300 m from Aug 2"
 *   6 clubmates     "3 clubmates swam this week"
 *   7 date          "Tuesday, September 9"
 *
 * Every number is one the Scores tab already shows, computed by the SAME code:
 * set lineage (times swum, fastest, the whole-set vs average-length basis)
 * comes from setHistory.setHistoryRows over the swimFacts cache entries
 * (allTimeAggregates), the farthest swim from allTimeAggregates.farthestSwim.
 * Nothing here re-derives a record from laps, so a card can never disagree
 * with the tab it was shared from (showcase-no-fork).
 *
 * "History" is the caller's problem to assemble: the swimFacts entries of
 * every swim the app has counted (this swim may or may not be among them;
 * it is removed by uuid before "prior" is computed) plus, optionally, the
 * plain workout list for the distance rule. Pure module: no store reads, no
 * Date.now(), no locale (dates format en-US so a card reads the same on
 * every device and in every test).
 */
import type { WorkoutSummary } from "@contract/types";
import { extractSwimFacts, farthestSwim, type SwimFactsEntry, type SwimFactsSet, type SwimFactsSource } from "../allTimeAggregates";
import { fmtMeters } from "../format";
import { medianDurationByStroke } from "../recordEligibility";
import { setHistoryRows, fmtSetValue, setMetersLabel, setShortName, type SetHistoryRow, type SetHistoryPoint } from "../setHistory";
import { SET_DISPLAY } from "../setLibrary";
import type { StoryLine } from "./types";

/** What the app knows about every OTHER swim. */
export interface StoryHistory {
  /** swimFacts cache entries (allTimeAggregates.getAllSwimFacts()). May include this swim; it is excluded by uuid. */
  facts: SwimFactsEntry[];
  /** The plain workout list, for the farthest-swim rule. Optional: without it rule 5 never fires. */
  workouts?: WorkoutSummary[];
}

/** Club context for rule 6 and the card's club line. */
export interface StoryClub {
  /** First names of clubmates who swam in the last seven days (self excluded). */
  swamThisWeek: string[];
}

/** Everything the seven rules read about THIS swim. */
export interface StoryInputs {
  uuid: string;
  /** ISO date or timestamp; only the calendar day is used. */
  date: string;
  distanceM: number;
  /** This swim's own facts, from extractSwimFacts (see factsForStory). */
  facts: SwimFactsEntry;
  history: StoryHistory;
  club?: StoryClub;
}

/** This swim's facts, extracted the way the Scores tab extracts them. Records
 *  are measured against the medians of the prior history so a set that was
 *  never a record on the tab is never a record on a card. */
export function factsForStory(src: SwimFactsSource, uuid: string, date: string, history: StoryHistory): SwimFactsEntry {
  const prior = history.facts.filter((e) => e.swimUuid !== uuid);
  return extractSwimFacts(src, uuid, date, medianDurationByStroke(prior));
}

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 11–13 → "th", 21 → "21st". */
export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

/** Calendar day from "YYYY-MM-DD" or a full ISO timestamp, as a LOCAL date so
 *  "2026-09-09" is September 9 in every timezone (new Date("2026-09-09") is
 *  UTC midnight, which is September 8 in Denver). A timestamp keeps its
 *  instant and takes the local day, like the rest of the app. */
function localDay(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(iso);
}

/** "Aug 2", or "Aug 2, 2025" when the year differs from the swim's. */
function shortDate(ms: number, swimYear: number): string {
  const d = new Date(ms);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (d.getFullYear() !== swimYear) opts.year = "numeric";
  return d.toLocaleDateString("en-US", opts);
}

/** "Tuesday, September 9" — the last-resort headline. */
export function dateLine(iso: string): string {
  return localDay(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

/** Named sets of one entry in swim order. */
function namedSets(e: SwimFactsEntry): SwimFactsSet[] {
  return [...(e.sets ?? [])].sort((a, b) => a.startMs - b.startMs);
}

/** Signature (club-named) sets first, then everyday shapes, each in swim order.
 *  Ruling 10: the set is the hero, and a Rockalita beats a Freestyle set. */
function candidateSets(e: SwimFactsEntry): SwimFactsSet[] {
  const sets = namedSets(e);
  const sig = sets.filter((s) => SET_DISPLAY[s.setId]?.isSignature);
  const rest = sets.filter((s) => !SET_DISPLAY[s.setId]?.isSignature);
  return [...sig, ...rest];
}

/** One set's lineage across every swim, this one included: the row plus this
 *  swim's own points and the best of the PRIOR swims on the same basis. */
export interface SetLineage {
  row: SetHistoryRow;
  /** This swim's occurrences (usually one). */
  mine: SetHistoryPoint[];
  /** Fastest prior occurrence on the row's basis; null when never swum before. */
  priorBest: SetHistoryPoint | null;
  priorTimes: number;
}

/** Lineage per setId, computed ONCE over prior + this swim. */
export function lineageFor(inputs: Pick<StoryInputs, "uuid" | "facts" | "history">): Map<string, SetLineage> {
  const prior = inputs.history.facts.filter((e) => e.swimUuid !== inputs.uuid);
  const rows = setHistoryRows([...prior, inputs.facts]);
  const out = new Map<string, SetLineage>();
  for (const row of rows) {
    const mine = row.series.filter((p) => p.workoutId === inputs.uuid);
    const others = row.series.filter((p) => p.workoutId !== inputs.uuid);
    const clean = others.filter((p) => !p.hasPhantom);
    const pool = clean.length > 0 ? clean : others;
    let priorBest: SetHistoryPoint | null = null;
    for (const p of pool) if (priorBest === null || p.valueS < priorBest.valueS) priorBest = p;
    out.set(row.setId, { row, mine, priorBest, priorTimes: others.length });
  }
  return out;
}

function displayName(setId: string, fallback: string): string {
  return SET_DISPLAY[setId]?.displayName ?? fallback;
}

/**
 * The seven-rule table. Exported under the C4 name.
 */
export function storyLineFor(inputs: StoryInputs): StoryLine {
  const swimYear = localDay(inputs.date).getFullYear();
  const lineage = lineageFor(inputs);
  const candidates = candidateSets(inputs.facts);

  // 1. new-set: a named set with no prior occurrence.
  for (const s of candidates) {
    const l = lineage.get(s.setId);
    if (!l || l.priorTimes > 0 || l.mine.length === 0) continue;
    const name = displayName(s.setId, s.label);
    return {
      kind: "new-set",
      text: `New set: first time you swam ${name}`,
      detail: `${setMetersLabel(l.row)} in ${fmtSetValue(l.row, l.mine[0].valueS)}`,
    };
  }

  // 2. fastest-set: swum before, and this swim holds the row's fastest.
  for (const s of candidates) {
    const l = lineage.get(s.setId);
    if (!l || l.priorTimes === 0 || l.priorBest === null) continue;
    if (l.row.fastest.workoutId !== inputs.uuid) continue;
    const name = setShortName(displayName(s.setId, s.label));
    return {
      kind: "fastest-set",
      text: `Fastest ${name} yet: ${fmtSetValue(l.row, l.row.fastest.valueS)}`,
      detail: `beat ${fmtSetValue(l.row, l.priorBest.valueS)} from ${shortDate(l.priorBest.dateMs, swimYear)}`,
    };
  }

  // 3. new-sequence: two or more named sets in an order no prior swim used.
  const seq = namedSets(inputs.facts).map((s) => s.setId);
  if (seq.length >= 2) {
    const key = seq.join(">");
    const prior = inputs.history.facts.filter((e) => e.swimUuid !== inputs.uuid && e.sets !== null);
    const seen = prior.some((e) => namedSets(e).map((s) => s.setId).join(">") === key);
    if (prior.length > 0 && !seen) {
      return {
        kind: "new-sequence",
        text: "New sequence",
        detail: seq.map((id) => setShortName(displayName(id, id))).join(" → "),
      };
    }
  }

  // 4. nth-time: lineage of the most-swum candidate (signature first).
  let best: { s: SwimFactsSet; l: SetLineage } | null = null;
  for (const s of candidates) {
    const l = lineage.get(s.setId);
    if (!l || l.priorTimes === 0) continue;
    const sig = SET_DISPLAY[s.setId]?.isSignature ?? false;
    const bestSig = best ? (SET_DISPLAY[best.s.setId]?.isSignature ?? false) : false;
    if (best === null || (sig && !bestSig) || (sig === bestSig && l.row.timesSwum > best.l.row.timesSwum)) {
      best = { s, l };
    }
  }
  if (best) {
    const name = displayName(best.s.setId, best.s.label);
    return {
      kind: "nth-time",
      text: `${ordinal(best.l.row.timesSwum)} time swimming ${name}`,
      detail: `best ${fmtSetValue(best.l.row, best.l.row.fastest.valueS)}`,
    };
  }

  // 5. pr-distance: farther than every prior workout.
  const priorWorkouts = (inputs.history.workouts ?? []).filter((w) => w.uuid !== inputs.uuid);
  const far = farthestSwim(priorWorkouts);
  if (far && inputs.distanceM > far.meters) {
    return {
      kind: "pr-distance",
      text: `Farthest swim yet: ${fmtMeters(inputs.distanceM)}`,
      detail: `beat ${fmtMeters(far.meters)} from ${shortDate(new Date(far.startDate).getTime(), swimYear)}`,
    };
  }

  // 6. clubmates this week.
  const mates = inputs.club?.swamThisWeek ?? [];
  if (mates.length > 0) {
    return {
      kind: "clubmates",
      text: `${mates.length} clubmate${mates.length === 1 ? "" : "s"} swam this week`,
      detail: mates.join(", "),
    };
  }

  // 7. the date.
  return { kind: "date", text: dateLine(inputs.date) };
}

/**
 * The story on a SET card, naming the set so a sheet headline can carry it:
 * "Rockalita swum 7 times, best 4:31" with the comparison as the detail;
 * "First Rockalita: 3:52" for a single occurrence (no "best", no "since":
 * one swim is neither); "New set" when the history has never seen it. A set
 * with no ledger id (a Mixed set, a lone length) has no lineage and reads as
 * new.
 */
export function setStoryLineFor(setId: string | null, history: StoryHistory): StoryLine {
  if (setId === null) return { kind: "new-set", text: "New set" };
  const row = setHistoryRows(history.facts).find((r) => r.setId === setId);
  if (!row) return { kind: "new-set", text: "New set" };
  const name = setShortName(row.displayName);
  if (row.timesSwum === 1) {
    return { kind: "new-set", text: `First ${name}: ${fmtSetValue(row, row.first.valueS)}` };
  }
  const times = `${name} swum ${row.timesSwum} times`;
  const year = new Date(row.last.dateMs).getFullYear();
  // The comparison the swim card carries: when the latest swim of this set is
  // its fastest, say what it beat; otherwise, how far back the lineage goes.
  const latestIsFastest = row.fastest.workoutId === row.last.workoutId;
  const earlier = row.series.filter((p) => p.workoutId !== row.last.workoutId);
  const clean = earlier.filter((p) => !p.hasPhantom);
  let prev: SetHistoryPoint | null = null;
  for (const p of clean.length > 0 ? clean : earlier) if (prev === null || p.valueS < prev.valueS) prev = p;
  const detail =
    latestIsFastest && prev !== null
      ? `beat ${fmtSetValue(row, prev.valueS)} from ${shortDate(prev.dateMs, year)}`
      : `since ${shortDate(row.first.dateMs, year)}`;
  return { kind: "nth-time", text: `${times}, best ${fmtSetValue(row, row.fastest.valueS)}`, detail };
}
